import type { Variant } from '../gameTypes.js';
import { VARIANTS, parseVariantSlug } from '../gameTypes.js';
import type { GuitarChart } from '../guitarChart.js';
import { ON_AND_ON_GUITAR_VARIANTS } from './01-on-and-on.js';
import { SKY_HIGH_GUITAR_VARIANTS } from './02-sky-high.js';
import { HEROES_GUITAR_VARIANTS } from './03-heroes.js';
import { CRADLES_GUITAR_VARIANTS } from './04-cradles.js';
import { MY_HEART_GUITAR_VARIANTS } from './05-my-heart.js';
import { INVINCIBLE_GUITAR_VARIANTS } from './06-invincible.js';
import { HOPE_GUITAR_VARIANTS } from './07-hope.js';
import { ADVENTURE_GUITAR_VARIANTS } from './08-adventure.js';
import { BLANK_GUITAR_VARIANTS } from './09-blank.js';
import { DIAMOND_GUITAR_VARIANTS } from './10-diamond.js';
import { HOLLAH_GUITAR_VARIANTS } from './11-hollah.js';
import { NEKOZILLA_GUITAR_VARIANTS } from './12-nekozilla.js';

/**
 * One playable Guitar map: a single (track, variant) pair. Mirrors the
 * Pulse / Circles / Piano builtin shapes so the seeder, browse pages,
 * and play page can iterate any catalogue uniformly.
 *
 * Charts here are built from real osu!mania 4K community sources via
 * `tools/RebuildGuitarFromOsu.ts`. Lineup matches the 12 baseSlug
 * slots used by Piano / Circles, with substitutions documented inline
 * in `tools/guitarBeatmapManifest.ts` for slugs whose original NCS
 * audio has no playable mania coverage.
 */
export interface GuitarBuiltinMap {
  chart: GuitarChart;
  expectedReplay: null;
}

export interface GuitarBuiltinTrack {
  baseSlug: string;
  variants: Record<Variant, GuitarBuiltinMap>;
}

const VARIANT_BUNDLES: ReadonlyArray<{
  baseSlug: string;
  charts: Record<Variant, GuitarChart>;
}> = [
  { baseSlug: 'on-and-on', charts: ON_AND_ON_GUITAR_VARIANTS },
  { baseSlug: 'sky-high', charts: SKY_HIGH_GUITAR_VARIANTS },
  { baseSlug: 'heroes', charts: HEROES_GUITAR_VARIANTS },
  { baseSlug: 'cradles', charts: CRADLES_GUITAR_VARIANTS },
  { baseSlug: 'my-heart', charts: MY_HEART_GUITAR_VARIANTS },
  { baseSlug: 'invincible', charts: INVINCIBLE_GUITAR_VARIANTS },
  { baseSlug: 'hope', charts: HOPE_GUITAR_VARIANTS },
  { baseSlug: 'adventure', charts: ADVENTURE_GUITAR_VARIANTS },
  { baseSlug: 'blank', charts: BLANK_GUITAR_VARIANTS },
  { baseSlug: 'diamond', charts: DIAMOND_GUITAR_VARIANTS },
  { baseSlug: 'hollah', charts: HOLLAH_GUITAR_VARIANTS },
  { baseSlug: 'nekozilla', charts: NEKOZILLA_GUITAR_VARIANTS },
];

export const BUILTIN_GUITAR_TRACKS: GuitarBuiltinTrack[] = VARIANT_BUNDLES.map(
  (bundle) => ({
    baseSlug: bundle.baseSlug,
    variants: Object.fromEntries(
      VARIANTS.map((v) => [
        v,
        {
          chart: bundle.charts[v],
          expectedReplay: null,
        } satisfies GuitarBuiltinMap,
      ]),
    ) as Record<Variant, GuitarBuiltinMap>,
  }),
);

export const BUILTIN_GUITAR_MAPS: GuitarBuiltinMap[] = BUILTIN_GUITAR_TRACKS.flatMap(
  (track) => VARIANTS.map((v) => track.variants[v]),
);

/**
 * Look up a Guitar builtin by slug (`'heroes'`, `'heroes-hard'`,
 * `'heroes-extreme'`). Mirrors the Piano resolver: literal-then-variant
 * order so a hypothetical track whose slug ends in `-hard` won't be
 * misparsed.
 */
export function getBuiltinGuitarMapBySlug(slug: string): GuitarBuiltinMap | undefined {
  const literalTrack = BUILTIN_GUITAR_TRACKS.find((t) => t.baseSlug === slug);
  if (literalTrack) {
    return literalTrack.variants.normal;
  }
  const { baseSlug, variant } = parseVariantSlug(slug);
  const track = BUILTIN_GUITAR_TRACKS.find((t) => t.baseSlug === baseSlug);
  if (!track) {
    return undefined;
  }
  return track.variants[variant];
}

export function listBuiltinGuitarTracks(): GuitarBuiltinTrack[] {
  return BUILTIN_GUITAR_TRACKS;
}

export function listBuiltinGuitarMaps(): GuitarBuiltinMap[] {
  return BUILTIN_GUITAR_MAPS;
}
